import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, Send, CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

const EMAIL_TYPE_LABELS = {
  pf_review_notification: 'PaymentFile Review',
};

const formatDT = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const formatCurrency = (v) => {
  if (v === null || v === undefined) return '—';
  try { return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; } catch { return String(v); }
};

export default function EmailJobsPage({ accounts = [] }) {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [accountId, setAccountId] = useState('all');
  const [emailType, setEmailType] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);
  const PAGE = 50;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset };
      if (accountId !== 'all') params.account_id = accountId;
      if (emailType !== 'all') params.email_type = emailType;
      if (status !== 'all') params.status = status;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get('/cockpit/email-jobs', { params });
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load email jobs');
    } finally {
      setLoading(false);
    }
  }, [accountId, emailType, status, search, dateFrom, dateTo, offset]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  return (
    <div className="space-y-4" data-testid="email-jobs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Email Jobs</h2>
          <p className="text-xs text-muted-foreground">Audit log of platform-sent emails (PaymentFile Review notifications, etc.)</p>
        </div>
        <Button onClick={() => { setOffset(0); fetchJobs(); }} variant="outline" size="sm" className="rounded-none" data-testid="email-jobs-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap border border-border bg-card p-3 rounded-none">
        <Select value={accountId} onValueChange={(v) => { setAccountId(v); setOffset(0); }}>
          <SelectTrigger className="w-56 rounded-none h-8 text-xs" data-testid="email-jobs-account-filter">
            <SelectValue placeholder="All organisations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All organisations</SelectItem>
            {accounts.map(a => (
              <SelectItem key={a.id} value={a.id}>{a.organisation_name || a.name || a.account_no}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={emailType} onValueChange={(v) => { setEmailType(v); setOffset(0); }}>
          <SelectTrigger className="w-48 rounded-none h-8 text-xs" data-testid="email-jobs-type-filter">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="pf_review_notification">PaymentFile Review</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setOffset(0); }}>
          <SelectTrigger className="w-32 rounded-none h-8 text-xs" data-testid="email-jobs-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
          className="w-36 rounded-none h-8 text-xs"
          placeholder="From"
          data-testid="email-jobs-date-from"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
          className="w-36 rounded-none h-8 text-xs"
          placeholder="To"
          data-testid="email-jobs-date-to"
        />
        <Input
          placeholder="Search email / subject / PF..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setOffset(0); fetchJobs(); } }}
          className="w-64 rounded-none h-8 text-xs"
          data-testid="email-jobs-search"
        />
        <span className="text-xs text-muted-foreground ml-auto">{total} job(s)</span>
      </div>

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="email-jobs-table">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <th className="text-left px-4 py-2.5 font-semibold">Sent At</th>
              <th className="text-left px-4 py-2.5 font-semibold">Organisation</th>
              <th className="text-left px-4 py-2.5 font-semibold">Type</th>
              <th className="text-left px-4 py-2.5 font-semibold">Related</th>
              <th className="text-left px-4 py-2.5 font-semibold">Recipients</th>
              <th className="text-left px-4 py-2.5 font-semibold">Status</th>
              <th className="text-center px-4 py-2.5 font-semibold w-10"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const isOpen = expandedId === j.job_id;
              return (
                <>
                  <tr key={j.job_id} className="border-b border-border/60 hover:bg-muted/20 transition-colors" data-testid={`email-job-row-${j.job_id}`}>
                    <td className="px-4 py-2.5 text-xs font-mono whitespace-nowrap">{formatDT(j.sent_at)}</td>
                    <td className="px-4 py-2.5 text-sm">{j.organisation_name || '—'}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className="text-[10px] rounded-none bg-blue-500/10 text-blue-700 border-blue-500/20">
                        <Send className="h-2.5 w-2.5 mr-1" />{EMAIL_TYPE_LABELS[j.email_type] || j.email_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-sm font-medium">{j.related_entity_name || '—'}</td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className="font-mono">{(j.to_addresses || []).length}</span>
                      <span className="text-muted-foreground ml-1 truncate inline-block max-w-[260px] align-bottom" title={(j.to_addresses || []).join(', ')}>
                        · {(j.to_addresses || []).slice(0, 2).join(', ')}{(j.to_addresses || []).length > 2 ? ` +${j.to_addresses.length - 2}` : ''}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {j.status === 'sent' ? (
                        <Badge className="rounded-none text-[10px] bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-2.5 w-2.5 mr-1" />Sent</Badge>
                      ) : (
                        <Badge className="rounded-none text-[10px] bg-red-100 text-red-700"><XCircle className="h-2.5 w-2.5 mr-1" />Failed</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 rounded-none"
                        onClick={() => setExpandedId(isOpen ? null : j.job_id)}
                        data-testid={`email-job-expand-${j.job_id}`}
                      >
                        {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </Button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-muted/20 border-b border-border/60" data-testid={`email-job-detail-${j.job_id}`}>
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-2 gap-6 text-xs">
                          <div>
                            <div className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground mb-2">Subject</div>
                            <div className="mb-3">{j.subject}</div>
                            <div className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground mb-2">Metadata</div>
                            {j.metadata ? (
                              <div className="space-y-0.5">
                                {j.metadata.total_requested !== undefined && <div>Total Requested: <span className="font-mono">{formatCurrency(j.metadata.total_requested)}</span></div>}
                                {j.metadata.invoice_count !== undefined && <div>Invoices: <span className="font-mono">{j.metadata.invoice_count}</span></div>}
                                {j.metadata.nearest_due_date && <div>Nearest Due: <span className="font-mono">{j.metadata.nearest_due_date}</span></div>}
                              </div>
                            ) : <span className="text-muted-foreground">—</span>}
                            {j.error && (
                              <>
                                <div className="font-semibold uppercase tracking-wider text-[10px] text-red-600 mb-2 mt-3">Error</div>
                                <div className="font-mono text-red-700 break-all">{j.error}</div>
                              </>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground mb-2">Recipients</div>
                            <div className="space-y-1 max-h-48 overflow-auto">
                              {(j.recipients || []).map((r, i) => (
                                <div key={i} className="flex items-center justify-between gap-2 border-b border-border/40 py-1">
                                  <span className="font-mono">{r.email}</span>
                                  {r.status === 'sent' ? (
                                    <Badge className="rounded-none text-[9px] bg-emerald-100 text-emerald-700">sent</Badge>
                                  ) : (
                                    <Badge className="rounded-none text-[9px] bg-red-100 text-red-700" title={r.error || ''}>failed</Badge>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {jobs.length === 0 && !loading && (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No email jobs found</td></tr>
            )}
            {loading && jobs.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline" size="sm" className="rounded-none"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
            data-testid="email-jobs-prev"
          >Previous</Button>
          <span className="text-xs text-muted-foreground">{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <Button
            variant="outline" size="sm" className="rounded-none"
            disabled={offset + PAGE >= total}
            onClick={() => setOffset(offset + PAGE)}
            data-testid="email-jobs-next"
          >Next</Button>
        </div>
      )}
    </div>
  );
}
